import {
  ArrowUpRight,
  BadgeCheck,
  Calculator,
  ChevronRight,
  ClipboardCheck,
  GitCompare,
  MemoryStick,
  Radio,
  Scale,
  Tv,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import type { Messages } from "@/lib/i18n";

type GuideItem = {
  slug: string;
  href?: string;
  icon: LucideIcon;
  titleKey: keyof Messages;
  descKey: keyof Messages;
  ctaKey?: keyof Messages;
};

const guides: ReadonlyArray<GuideItem> = [
  {
    slug: "iptv-stb-buyers-checklist",
    icon: ClipboardCheck,
    titleKey: "guidesCardSbcTitle",
    descKey: "guidesCardSbcDesc",
  },
  {
    slug: "stb-tco-calculator",
    href: "/tools/stb-tco-calculator",
    icon: Calculator,
    titleKey: "tcoCardTitle",
    descKey: "tcoCardDesc",
    ctaKey: "tcoCardCta",
  },
  {
    slug: "stb-ram-and-storage",
    icon: MemoryStick,
    titleKey: "guidesCardRsgTitle",
    descKey: "guidesCardRsgDesc",
  },
  {
    slug: "android-vs-linux-stb",
    icon: GitCompare,
    titleKey: "guidesCardAvlTitle",
    descKey: "guidesCardAvlDesc",
  },
  {
    slug: "aosp-vs-certified-android-tv",
    icon: BadgeCheck,
    titleKey: "guidesCardAcTitle",
    descKey: "guidesCardAcDesc",
  },
  {
    slug: "own-stb-vs-smart-tv",
    icon: Tv,
    titleKey: "guidesCardOsTitle",
    descKey: "guidesCardOsDesc",
  },
  {
    slug: "subsidize-lease-or-sell",
    icon: Wallet,
    titleKey: "guidesCardBmTitle",
    descKey: "guidesCardBmDesc",
  },
  {
    slug: "multicast-iptv-vs-ott",
    icon: Radio,
    titleKey: "guidesCardMoTitle",
    descKey: "guidesCardMoDesc",
  },
  {
    slug: "build-vs-buy-stb",
    icon: Scale,
    titleKey: "guidesCardBbTitle",
    descKey: "guidesCardBbDesc",
  },
];

export function GuidesIndexPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.navGuides}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60 bg-muted/10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-20"
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <p className="animate-fade-up text-sm font-medium text-primary">
            {messages.guidesIndexEyebrow}
          </p>
          <h1
            className="mt-3 animate-fade-up text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.guidesIndexTitle}
          </h1>
          <p
            className="mx-auto mt-5 max-w-2xl animate-fade-up text-lg text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            {messages.guidesIndexSubtitle}
          </p>
        </div>
      </section>

      {/* Guides grid */}
      <section className="mx-auto max-w-7xl px-6 py-14 lg:py-20">
        <h2 className="animate-fade-up text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {messages.guidesIndexAllTitle}
        </h2>
        <div
          className="mt-8 grid animate-fade-up grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
          style={{ animationDelay: "80ms" }}
        >
          {guides.map((item) => {
            const Icon = item.icon;
            return (
              <a
                key={item.slug}
                href={u(item.href ?? `/guides/${item.slug}`)}
                className="group relative flex flex-col rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-soft)] transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[var(--shadow-elevated)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon aria-hidden className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary">
                  {messages[item.titleKey]}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {messages[item.descKey]}
                </p>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-primary">
                  {messages[item.ctaKey ?? "guidesIndexCardCta"]}
                  <ArrowUpRight
                    aria-hidden
                    className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </span>
              </a>
            );
          })}
        </div>
      </section>
    </SiteLayout>
  );
}
